import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { AlertListRow } from 'components/ui/AlertListRow/AlertListRow'
import { AlertList } from 'components/ui/AlertListRow/AlertListRow.styled'
import { ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { useNavigate } from 'react-router-dom'
import { Flex, MainHeadline, PageWrapper } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import useQueryPagination from 'hooks/useQueryPagination'
import { getParams } from 'utils/helpers'
import {
  clearInventoryDashboard,
  getInventoryItemsDashboard,
} from 'features/inventory/inventoryItemsSlice'
import {
  getInventoryReplenishmentSuggestions,
  getInventoryStockValuation,
} from 'features/inventory/inventoryControlSlice'
import { IInvReorderRowVM } from 'types/inventoryOps.interface'
import { formatInvGhs } from '../Shared/inventoryFormatters'
import {
  CriticalNums,
  CriticalRow,
  DaysText,
  HeaderLink,
  InvGrid,
  InvKpiStrip,
  InvWidget,
  ItemCellMeta,
  ItemCellName,
  ItemCellText,
  ReorderTableTight,
  WidgetBody,
  WidgetLoader,
} from './InventoryDashboard.styled'
import { RaiseProcurementRequestModal } from './RaiseProcurementRequestModal'

const daysColor = (days: number | null): string => {
  if (days == null) return tokens.text.muted
  if (days < 15) return tokens.status.danger.fg
  if (days < 30) return tokens.status.warning.fg
  return tokens.text.muted
}

const daysLabel = (days: number | null): string => (days == null ? '—' : `${days} d`)

export const InventoryDashboard = () => {
  const { dashboard, isLoading } = useSelector((store: RootState) => store.inventoryItems)
  const { valuation, replenishmentSuggestions } = useSelector(
    (store: RootState) => store.inventoryControl,
  )
  const [params] = useState<any>(getParams({ location }))
  const [reorderModalRow, setReorderModalRow] = useState<IInvReorderRowVM | null>(null)
  const navigate = useNavigate()

  useQueryPagination({
    fetchFunctions: [
      getInventoryItemsDashboard,
      getInventoryStockValuation,
      getInventoryReplenishmentSuggestions,
    ],
    cleanUpFunctions: [clearInventoryDashboard],
    params,
  })

  const reorderRows: IInvReorderRowVM[] = useMemo(() => {
    const suggestions = replenishmentSuggestions.data ?? []
    if (suggestions.length) {
      return suggestions
        .filter((s) => !s.resolved)
        .map((s) => ({
          id: s.itemId,
          sku: s.sku,
          name: s.name,
          critical: s.critical,
          onHand: s.onHand,
          minLevel: s.minLevel ?? 0,
          daysToStockout: s.daysToStockout,
          leadTimeDays: s.leadTimeDays,
          suggestedQty: s.suggestedQty,
          belowMin: true,
        }))
        .sort((a, b) => (a.daysToStockout ?? 999) - (b.daysToStockout ?? 999))
    }
    if (dashboard?.data?.length) {
      return dashboard.data
        .map((d) => ({
          id: d.id,
          sku: d.sku,
          name: d.name,
          critical: Boolean(d.critical),
          onHand: d.quantity,
          minLevel: d.minLevel,
          daysToStockout: null,
          leadTimeDays: d.leadTimeDays ?? null,
          suggestedQty: null,
          belowMin: Boolean(d.isLowStock) || d.quantity < d.minLevel,
        }))
        .filter((r) => r.belowMin)
        .sort((a, b) => a.onHand / (a.minLevel || 1) - b.onHand / (b.minLevel || 1))
    }
    return []
  }, [dashboard, replenishmentSuggestions.data])

  const criticalItems = useMemo(
    () => (dashboard?.data ?? []).filter((d) => d.critical || d.isLowStock).slice(0, 8),
    [dashboard],
  )

  const slowMovers = useMemo(
    () => (dashboard?.data ?? []).filter((d) => d.movementClass === 2 || d.movementClass === 3).slice(0, 8),
    [dashboard],
  )

  const locations = valuation?.locations ?? []

  const reorderColumns: IHonestTableColumn<IInvReorderRowVM>[] = [
    {
      id: 'item',
      header: 'ITEM',
      fill: true,
      cell: (row) => (
        <Flex center gap='0.5rem'>
          <ItemCellText>
            <ItemCellName>{row.name}</ItemCellName>
            <ItemCellMeta>{row.sku}</ItemCellMeta>
          </ItemCellText>
          {row.critical && <StatusPill status='accent'>Critical</StatusPill>}
        </Flex>
      ),
    },
    { id: 'onHand', header: 'ON HAND', cell: (row) => row.onHand },
    { id: 'min', header: 'MIN', cell: (row) => row.minLevel },
    {
      id: 'days',
      header: 'DAYS LEFT',
      cell: (row) => (
        <DaysText $color={daysColor(row.daysToStockout)}>{daysLabel(row.daysToStockout)}</DaysText>
      ),
    },
    {
      id: 'suggest',
      header: 'SUGGESTED',
      cell: (row) => (row.suggestedQty != null ? row.suggestedQty : '—'),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <Button size='sm' variant='secondary' onClick={() => setReorderModalRow(row)}>
          Raise PR
        </Button>
      ),
    },
  ]

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex between center mb='16'>
          <MainHeadline>Inventory</MainHeadline>
        </Flex>

        <InvKpiStrip>
          <MetricCard
            label='Site stock value'
            value={formatInvGhs(valuation?.totalValueGhs ?? 0)}
          />
          <MetricCard label='Below min' value={String(valuation?.belowMinCount ?? dashboard?.lowStockCount ?? 0)} />
          <MetricCard label='SKUs' value={String(valuation?.skuCount ?? dashboard?.totalCount ?? 0)} />
          <MetricCard
            label='Reserved value'
            value={formatInvGhs(valuation?.reservedValueGhs ?? 0)}
            sublabel={`${valuation?.reservedCommitments ?? 0} commitments`}
          />
          <MetricCard label='Slow / obsolete' value={String(valuation?.slowObsoleteCount ?? 0)} />
        </InvKpiStrip>

        <InvGrid>
          <InvWidget $span={6}>
            <ContainerHeader>
              <Flex between center width='100%'>
                <TitleText>Reorder</TitleText>
                <HeaderLink onClick={() => navigate('/inventory/stock')}>Stock →</HeaderLink>
              </Flex>
            </ContainerHeader>
            <ContainerContent>
              <WidgetBody>
                {isLoading ? (
                  <WidgetLoader>Loading…</WidgetLoader>
                ) : !reorderRows.length ? (
                  <NoItems title='Reorder lines' />
                ) : (
                  <ReorderTableTight>
                    <HonestTable columns={reorderColumns} data={reorderRows} keyField='id' />
                  </ReorderTableTight>
                )}
              </WidgetBody>
            </ContainerContent>
          </InvWidget>

          <InvWidget $span={6}>
            <ContainerHeader>
              <TitleText>Critical / low</TitleText>
            </ContainerHeader>
            <ContainerContent>
              <WidgetBody>
                {!criticalItems.length ? (
                  <NoItems title='Critical items' />
                ) : (
                  <AlertList>
                    {criticalItems.map((item) => (
                      <CriticalRow key={item.id}>
                        <AlertListRow
                          title={item.name}
                          meta={item.sku}
                          onClick={() => navigate('/inventory/stock')}
                        />
                        <CriticalNums>{item.quantity}</CriticalNums>
                      </CriticalRow>
                    ))}
                  </AlertList>
                )}
              </WidgetBody>
            </ContainerContent>
          </InvWidget>

          <InvWidget $span={6}>
            <ContainerHeader>
              <TitleText>Slow movers</TitleText>
            </ContainerHeader>
            <ContainerContent>
              <WidgetBody>
                {!slowMovers.length ? (
                  <NoItems title='Slow movers' />
                ) : (
                  <AlertList>
                    {slowMovers.map((item) => (
                      <AlertListRow
                        key={item.id}
                        title={item.name}
                        meta={item.sku}
                        onClick={() => navigate('/inventory/stock')}
                      />
                    ))}
                  </AlertList>
                )}
              </WidgetBody>
            </ContainerContent>
          </InvWidget>

          <InvWidget $span={6}>
            <ContainerHeader>
              <TitleText>Value by location</TitleText>
            </ContainerHeader>
            <ContainerContent>
              <WidgetBody>
                {!locations.length ? (
                  <NoItems title='Locations' />
                ) : (
                  <AlertList>
                    {locations.map((loc) => (
                      <CriticalRow key={loc.locationId}>
                        <AlertListRow title={loc.location} meta={`${loc.itemCount} SKUs`} />
                        <CriticalNums>{formatInvGhs(loc.valueGhs)}</CriticalNums>
                      </CriticalRow>
                    ))}
                  </AlertList>
                )}
              </WidgetBody>
            </ContainerContent>
          </InvWidget>
        </InvGrid>

        {reorderModalRow && (
          <RaiseProcurementRequestModal
            context={{
              inventoryItemId: Number(reorderModalRow.id),
              name: reorderModalRow.name,
              sku: reorderModalRow.sku,
              suggestedQty: reorderModalRow.suggestedQty,
              onHand: reorderModalRow.onHand,
              minLevel: reorderModalRow.minLevel,
            }}
            closeModal={() => setReorderModalRow(null)}
          />
        )}
      </PageWrapper>
    </>
  )
}
